/**
 * Global App Header Component
 * Shows on all pages with user info and logout button
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  LogOut,
  User,
  Home,
  Building2,
  Calculator,
  Settings,
  Menu,
  X,
  ChevronDown,
  Briefcase,
  Command,
  Inbox
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import CommandBarButton from './CommandBarButton';
import MultiChannelInbox from './MultiChannelInbox';

export default function AppHeader({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showInbox, setShowInbox] = useState(false);

  // Check localStorage for user on mount and route changes
  useEffect(() => {
    const checkUser = () => {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      } else {
        setUser(null);
      }
    };
    
    checkUser();
    
    // Also listen for storage events (for cross-tab updates)
    window.addEventListener('storage', checkUser);
    
    // Check periodically for login state changes
    const interval = setInterval(checkUser, 1000);
    
    return () => {
      window.removeEventListener('storage', checkUser);
      clearInterval(interval);
    };
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('ai_estimator_conversation');
    setUser(null);
    setShowUserMenu(false);
    if (onLogout) onLogout();
    navigate('/');
  };

  // Show header on all pages including landing
  const isLandingPage = location.pathname === '/';
  const textColor = isLandingPage ? 'text-white' : 'text-gray-600';
  const textColorHover = isLandingPage ? 'hover:text-yellow-300' : 'hover:text-emerald-600';
  const brandColor = isLandingPage ? 'text-white' : 'text-emerald-600';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isLandingPage ? 'bg-black/20 backdrop-blur-xl border-b border-white/5' : 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand */}
          <button
            onClick={() => navigate('/')}
            className={`flex items-center gap-2 ${brandColor} font-bold text-lg ${textColorHover} transition-colors`}
          >
            <Building2 className="w-6 h-6" />
            <span className="hidden sm:inline">jobmanager.ai</span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className={`${textColor} ${textColorHover} ${location.pathname === '/' && !isLandingPage ? 'text-emerald-600 bg-emerald-50' : ''}`}
            >
              <Home className="w-4 h-4 mr-1" />
              Home
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/jobs')}
              className={`${textColor} ${textColorHover} ${location.pathname === '/jobs' ? 'text-emerald-600 bg-emerald-50' : ''}`}
            >
              <Briefcase className="w-4 h-4 mr-1" />
              Jobs
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/free-estimator')}
              className={`${textColor} ${textColorHover} ${location.pathname === '/free-estimator' ? 'text-emerald-600 bg-emerald-50' : ''}`}
            >
              <Calculator className="w-4 h-4 mr-1" />
              Estimator
            </Button>
            {/* Dashboard - route based on user role */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (user) {
                  // Route to appropriate dashboard based on role
                  if (user.role === 'ceo' || user.role === 'admin') {
                    navigate('/dashboard');
                  } else if (user.role === 'client') {
                    navigate('/construction');
                  } else {
                    navigate('/dashboard');
                  }
                } else {
                  navigate('/login');
                }
              }}
              className={`${textColor} ${textColorHover} ${location.pathname.includes('/dashboard') || location.pathname === '/construction' ? 'text-emerald-600 bg-emerald-50' : ''}`}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            {/* My Projects - for all users */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/construction-projects')}
              className={`${textColor} ${textColorHover} ${location.pathname === '/construction-projects' ? 'text-emerald-600 bg-emerald-50' : ''}`}
            >
              <Home className="w-4 h-4 mr-1" />
              Projects
            </Button>
          </nav>

          {/* User Section */}
          <div className="flex items-center gap-2">
            {/* Inbox Button - desktop only for logged-in users */}
            {user && (
              <button
                onClick={() => setShowInbox(true)}
                className={`hidden md:flex p-2 rounded-lg transition-all relative ${isLandingPage ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                title="Multi-Channel Inbox"
                data-testid="inbox-btn"
              >
                <Inbox className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  3
                </span>
              </button>
            )}
            
            {/* Command Bar Button - desktop only */}
            <div className="hidden md:block">
              <CommandBarButton isLandingPage={isLandingPage} />
            </div>
            
            {/* Language Selector - desktop only */}
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isLandingPage ? 'bg-white/20 hover:bg-white/30' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-semibold text-sm">
                    {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className={`text-sm font-medium ${isLandingPage ? 'text-white' : 'text-gray-900'} truncate max-w-[120px]`}>
                      {user.name || user.email?.split('@')[0]}
                    </p>
                    <p className={`text-xs ${isLandingPage ? 'text-gray-200' : 'text-gray-500'} capitalize`}>{user.role || 'User'}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 ${isLandingPage ? 'text-white' : 'text-gray-500'} transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.name || 'User'}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full capitalize">
                        {user.role || 'User'}
                      </span>
                    </div>
                    
                    <div className="py-1">
                      {/* My Dashboard - role-based */}
                      <button
                        onClick={() => { 
                          if (user.role === 'ceo' || user.role === 'admin') {
                            navigate('/dashboard');
                          } else {
                            navigate('/construction');
                          }
                          setShowUserMenu(false); 
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Building2 className="w-4 h-4" />
                        My Dashboard
                      </button>
                      
                      {/* My Projects */}
                      <button
                        onClick={() => { navigate('/construction-projects'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Home className="w-4 h-4" />
                        My Projects
                      </button>
                      
                      {/* Profile */}
                      <button
                        onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </button>
                      
                      {/* Settings - only for admin/CEO */}
                      {(user.role === 'ceo' || user.role === 'admin') && (
                        <button
                          onClick={() => { navigate('/company-settings'); setShowUserMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Settings className="w-4 h-4" />
                          Company Settings
                        </button>
                      )}
                    </div>
                    
                    <div className="border-t border-gray-100 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className={`hidden sm:flex ${textColor} ${textColorHover} text-sm`}
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold text-xs sm:text-sm px-3 sm:px-4 h-9"
                >
                  Get Started
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`md:hidden p-2 rounded-lg ${isLandingPage ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-100'}`}
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Full screen slide-down */}
        {showMobileMenu && (
          <div className={`md:hidden border-t py-3 ${isLandingPage ? 'bg-black/90 backdrop-blur-xl border-white/10' : 'bg-white border-gray-200'}`}>
            <nav className="flex flex-col gap-0.5 px-2">
              {[
                { label: 'Home', icon: Home, path: '/' },
                { label: 'Jobs', icon: Briefcase, path: '/jobs' },
                { label: 'Free Estimator', icon: Calculator, path: '/free-estimator' },
                { label: 'Crown Pool — Invest', icon: null, path: '/crown-pool', highlight: true },
                {
                  label: 'Dashboard', icon: Building2, path: null,
                  action: () => {
                    if (user) {
                      navigate(user.role === 'ceo' || user.role === 'admin' ? '/dashboard' : '/construction');
                    } else { navigate('/login'); }
                    setShowMobileMenu(false);
                  }
                },
              ].map((item) => (
                <button
                  key={item.path || item.label}
                  onClick={item.action || (() => { navigate(item.path); setShowMobileMenu(false); })}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium min-h-[52px] transition-colors ${
                    item.highlight
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      : isLandingPage
                        ? 'text-white/90 hover:bg-white/10'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.highlight ? (
                    <span className="text-base">👑</span>
                  ) : item.icon ? (
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                  ) : null}
                  {item.label}
                </button>
              ))}

              {user && (
                <>
                  <div className={`my-2 border-t ${isLandingPage ? 'border-white/10' : 'border-gray-100'}`} />
                  <button
                    onClick={() => { navigate('/profile'); setShowMobileMenu(false); }}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium min-h-[52px] ${isLandingPage ? 'text-white/90 hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <User className="w-5 h-5" /> Profile
                  </button>
                  <button
                    onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium min-h-[52px] text-red-500 hover:bg-red-500/10"
                  >
                    <LogOut className="w-5 h-5" /> Log Out
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
      
      {/* Multi-Channel Inbox Modal */}
      <MultiChannelInbox isOpen={showInbox} onClose={() => setShowInbox(false)} />
    </header>
  );
}
